/**
 * Project Overview Service
 * Handles API calls for project overview functionality
 */

import { apiClient } from './api';

/**
 * Get comprehensive project overview data
 * @param {number} projectId - The project ID
 * @returns {Promise} - Overview data including all sections
 */
export const getProjectOverview = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/overview`);
    return response.data;
  } catch (error) {
    console.error('Error fetching project overview:', error);
    throw error;
  }
};

/**
 * Get quick overview status (for homepage and navigation)
 * @param {number} projectId - The project ID
 * @returns {Promise} - Quick status summary
 */
export const getOverviewStatus = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/overview/status`);
    return response.data;
  } catch (error) {
    console.error('Error fetching overview status:', error);
    throw error;
  }
};

/**
 * Link a component to the project overview
 * @param {number} projectId - The project ID
 * @param {string} componentType - Type: 'sow_insights', 'project_scope', 'project_timeline'
 * @param {number} componentId - The component ID to link
 * @returns {Promise} - Updated overview data
 */
export const linkComponentToOverview = async (projectId, componentType, componentId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/overview/link-component`, {
      component_type: componentType,
      component_id: componentId
    });
    return response.data;
  } catch (error) {
    console.error('Error linking component to overview:', error);
    throw error;
  }
};

/**
 * Manually recalculate overview completion percentage
 * @param {number} projectId - The project ID
 * @returns {Promise} - Updated overview with new completion percentage
 */
export const recalculateOverview = async (projectId) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/overview/recalculate`);
    return response.data;
  } catch (error) {
    console.error('Error recalculating overview:', error);
    throw error;
  }
};

/**
 * Get status icon and color based on section status
 * @param {string} status - Status: 'completed', 'in_progress', 'not_started'
 * @returns {Object} - Icon name and color
 */
export const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return { icon: 'CheckCircle', color: 'success' };
    case 'in_progress':
      return { icon: 'Warning', color: 'warning' };
    case 'not_started':
      return { icon: 'RadioButtonUnchecked', color: 'error' };
    default:
      return { icon: 'HelpOutline', color: 'default' };
  }
};

/**
 * Calculate overall completion message
 * @param {number} percentage - Completion percentage (0-100)
 * @param {boolean} isComplete - Whether overview is complete
 * @returns {string} - User-friendly message
 */
export const getCompletionMessage = (percentage, isComplete) => {
  if (isComplete) {
    return '✅ Project foundation is complete! You can now proceed to SAP methodology phases.';
  }

  if (percentage === 0) {
    return 'Get started by uploading your project documents and defining project scope.';
  }

  if (percentage < 50) {
    return `⚠️ ${percentage}% complete. Continue building your project foundation.`;
  }

  if (percentage < 100) {
    return `🔄 ${percentage}% complete. Almost there! Complete the remaining sections.`;
  }

  return `${percentage}% complete`;
};

/**
 * Get section priority for ordering
 * @param {string} sectionId - Section identifier
 * @returns {number} - Priority order (1 is highest)
 */
export const getSectionPriority = (sectionId) => {
  const priorities = {
    'sow-insights': 1,
    'project-scope': 2,
    'stakeholders': 3,
    'timeline': 4
  };
  return priorities[sectionId] || 999;
};

/**
 * Check if user can proceed to SAP phases
 * @param {Object} overviewStatus - Overview status object
 * @returns {Object} - { canProceed: boolean, message: string }
 */
export const canProceedToSAPPhases = (overviewStatus) => {
  if (!overviewStatus) {
    return {
      canProceed: false,
      message: 'Please complete the project overview first.'
    };
  }

  if (overviewStatus.is_complete) {
    return {
      canProceed: true,
      message: 'Project overview is complete. You can proceed!'
    };
  }

  const incompleteSections = [];
  const sections = overviewStatus.sections || {};

  if (sections.sow_insights !== 'completed') {
    incompleteSections.push('Project Insights');
  }
  if (sections.project_scope !== 'completed') {
    incompleteSections.push('Project Scope');
  }
  if (sections.stakeholders !== 'completed') {
    incompleteSections.push('Project Team');
  }
  if (sections.timeline !== 'completed') {
    incompleteSections.push('Project Timeline');
  }

  return {
    canProceed: false,
    message: `Please complete: ${incompleteSections.join(', ')}`,
    incompleteSections
  };
};

export default {
  getProjectOverview,
  getOverviewStatus,
  linkComponentToOverview,
  recalculateOverview,
  getStatusIcon,
  getCompletionMessage,
  getSectionPriority,
  canProceedToSAPPhases
};
